/**
 * <size-quickbuy>
 * ---------------------------------------------------------------------------
 * Companion element for the product-card hover size selector.
 *
 * On the product card the user sees:
 *   - Color swatches (.product-card__swatch-list)
 *   - A row of size buttons (rendered inside <size-quickbuy>)
 *
 * When the user hovers or selects a different color swatch, this script
 * re-maps each size button's hidden variant id input to the variant matching
 * the newly-active color + the button's size value. If a size is sold out
 * for the new color it is visually disabled.
 *
 * The Liquid embeds a variant map on the element as data-variant-map:
 *   [
 *     { "id": 12345, "size": "S", "color": "Black", "available": true },
 *     ...
 *   ]
 *
 * Each size button input carries:
 *   data-size-variant-input
 *   data-size-value="S"
 * ---------------------------------------------------------------------------
 */
(function () {
  if (window.customElements && window.customElements.get('size-quickbuy')) {
    return;
  }

  class SizeQuickbuy extends HTMLElement {
    connectedCallback() {
      this.productCard = this.closest('product-card');
      if (!this.productCard) {
        return;
      }

      try {
        this.variantMap = JSON.parse(this.dataset.variantMap || '[]');
      } catch (err) {
        this.variantMap = [];
      }

      this.currentColor = this.dataset.currentColor || null;

      this._onSwatchChange = this._onSwatchChange.bind(this);
      this._onSwatchPointerOver = this._onSwatchPointerOver.bind(this);

      this.productCard.addEventListener('change', this._onSwatchChange);
      this.productCard.addEventListener('pointerover', this._onSwatchPointerOver, true);
    }

    disconnectedCallback() {
      if (!this.productCard) {
        return;
      }
      this.productCard.removeEventListener('change', this._onSwatchChange);
      this.productCard.removeEventListener('pointerover', this._onSwatchPointerOver, true);
    }

    _onSwatchChange(event) {
      const target = event.target;
      if (!target || !target.matches) {
        return;
      }
      if (!target.matches('.product-card__swatch-list input[type="radio"]')) {
        return;
      }
      this._updateForColor(target.value);
    }

    _onSwatchPointerOver(event) {
      const target = event.target;
      if (!target || !target.closest) {
        return;
      }
      const label = target.closest('.product-card__swatch-list label[for]');
      if (!label) {
        return;
      }
      const id = label.getAttribute('for');
      if (!id) {
        return;
      }
      let input = null;
      try {
        input = this.productCard.querySelector('#' + CSS.escape(id));
      } catch (err) {
        return;
      }
      if (!input || !input.matches('.product-card__swatch-list input[type="radio"]')) {
        return;
      }
      this._updateForColor(input.value);
    }

    _updateForColor(colorValue) {
      if (!colorValue || colorValue === this.currentColor) {
        return;
      }
      this.currentColor = colorValue;

      const sizeInputs = this.querySelectorAll('[data-size-variant-input]');

      sizeInputs.forEach((input) => {
        const sizeValue = input.dataset.sizeValue;

        // Prefer exact color + size match
        let match = this.variantMap.find(function (v) {
          return v.size === sizeValue && v.color === colorValue;
        });

        // Fallback: any variant with this size (so the button still works
        // even if the color/size combo is not stocked, matching server-side behaviour)
        if (!match) {
          match = this.variantMap.find(function (v) {
            return v.size === sizeValue;
          });
        }

        if (!match) {
          return;
        }

        input.value = match.id;

        const form = input.closest('form');
        if (!form) {
          return;
        }

        const button = form.querySelector('.floating-size-selector__button, button[type="submit"]');
        if (!button) {
          return;
        }

        if (match.available) {
          button.classList.remove('is-disabled');
          button.removeAttribute('aria-disabled');
          button.disabled = false;
        } else {
          button.classList.add('is-disabled');
          button.setAttribute('aria-disabled', 'true');
          button.disabled = true;
        }
      });
    }
  }

  if (window.customElements && !window.customElements.get('size-quickbuy')) {
    window.customElements.define('size-quickbuy', SizeQuickbuy);
  }
})();